import React, { useMemo } from "react";
import { Navigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Paper, Typography } from "@mui/material";
import { RootState } from "../../../redux/store/store";
import {
  NavbarOptions,
  getFilteredModuleOptions,
} from "../../../routes/layouts/widget/constants";
import { theme } from "../../../common/utils/theme";

interface AffiliateRouteGuardProps {
  /**
   * The affiliate navbar option this route belongs to, matched against
   * `NavbarOptions.AFFILIATE_PORTAL.options[].path` -- e.g. "patients".
   * Detail routes reuse their section's path, so the invoice detail page is
   * guarded by "payments" just like the list it opens from.
   */
  optionPath: string;
  children: React.ReactElement;
}

const AccessDenied: React.FC = () => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "60vh",
      backgroundColor: theme?.palette?.secondary?.light,
      padding: 3,
    }}
  >
    <Paper
      elevation={3}
      sx={{
        padding: 4,
        maxWidth: "600px",
        textAlign: "center",
        borderRadius: 2,
      }}
    >
      <Typography
        variant="h5"
        sx={{ fontWeight: 600, marginBottom: 2, color: "error.main" }}
      >
        Access Restricted
      </Typography>
      <Typography variant="body1">
        You do not have permission to view this page. Please contact your
        administrator if you believe this is a mistake.
      </Typography>
    </Paper>
  </Box>
);

/**
 * Blocks affiliate portal routes the user cannot reach from the navbar.
 *
 * The allowed set comes from `getFilteredModuleOptions` -- the same helper the
 * navbar renders from -- so typing a path directly can never grant more than
 * the menu offers, and the two cannot drift apart as the rules change.
 */
const AffiliateRouteGuard: React.FC<AffiliateRouteGuardProps> = ({
  optionPath,
  children,
}) => {
  const user = useSelector((state: RootState) => state.auth.user);
  const permissions = useSelector((state: RootState) => state.auth.permissions);
  const { affiliateGroupUuid } = useParams();

  const allowedOptions = useMemo(
    () =>
      getFilteredModuleOptions(
        NavbarOptions.AFFILIATE_PORTAL.options,
        permissions,
        user,
      ),
    [permissions, user],
  );

  if (allowedOptions.some((option: any) => option.path === optionPath)) {
    return children;
  }

  // Send them to the first section they can actually open rather than a dead
  // end. The invoice detail route has no :affiliateGroupUuid of its own, so
  // fall back to the one on the profile -- the same value the navbar links use.
  const groupUuid = affiliateGroupUuid || (user as any)?.affiliateGroupUuid;
  const fallbackOption = allowedOptions[0];

  if (fallbackOption && groupUuid) {
    return (
      <Navigate
        to={`/affiliate-portal/affiliates/${groupUuid}/${fallbackOption.path}`}
        replace
      />
    );
  }

  return <AccessDenied />;
};

export default AffiliateRouteGuard;
